// 2次利用素材庫（design/README.md §6）。GET /api/assets（posts × deals × influencers）を状態フィルタ付きで表示。
import { useEffect, useState } from "react";
import type { GfAsset, GfAssetFilter, GfReuseState, GfTabProps, Loadable } from "../../../types/gifting";
import { fetchGfAssets } from "../../../api/giftingClient";
import { PLATFORM_LABEL, PLATFORM_SHORT, reuseScopeLabels, reuseState, REUSE_STATE_LABEL, fmtYmd } from "../../../lib/giftingRules";
import { GfChip, GfEmpty, GfErrorBanner, GfSkeleton, GfTag, type GfTone } from "../ui/GfUi";
import { errMsg, ymdSlash } from "./kit/helpers";
import { DetectedPosts } from "./assets/DetectedPosts";

const FILTERS: Array<{ value: GfAssetFilter; label: string }> = [
  { value: "all", label: "すべて" },
  { value: "granted", label: "許諾済" },
  { value: "requested", label: "申請中" },
  { value: "expiring", label: "期限間近" },
];

const STATE_TONE: Record<GfReuseState, GfTone> = {
  granted: "ok",
  expiring: "caution",
  expired: "warn",
  requested: "info",
  none: "muted",
};

const POST_TYPE_LABEL: Record<string, string> = {
  reel: "リール",
  reels: "リール",
  story: "ストーリー",
  stories: "ストーリー",
  feed: "フィード",
  photo: "写真",
  image: "写真",
  carousel: "カルーセル",
  video: "動画",
  short: "ショート",
  shorts: "ショート",
  post: "投稿",
};

function postTypeKey(a: GfAsset): string {
  return (a.post_type ?? "").trim().toLowerCase();
}

/** サムネのアスペクト比（媒体・投稿種別から。デザイン: 9/16・16/9・4/5・4/3） */
function ratioOf(a: GfAsset): string {
  const t = postTypeKey(a);
  const vertical = /reel|story|stories|short|リール|ストーリー|ショート/.test(t);
  switch (a.platform) {
    case "instagram":
      return vertical ? "9 / 16" : "4 / 5";
    case "tiktok":
      return "9 / 16";
    case "youtube":
      return vertical ? "9 / 16" : "16 / 9";
    case "ameblo":
      return "4 / 3";
    default:
      return "4 / 5";
  }
}

function kindLabel(a: GfAsset): string {
  const t = POST_TYPE_LABEL[postTypeKey(a)] ?? (a.post_type ?? "");
  if (a.platform === "instagram") return t ? `${PLATFORM_SHORT.instagram} ${t}` : PLATFORM_LABEL.instagram;
  return PLATFORM_LABEL[a.platform];
}

function termText(a: GfAsset, st: GfReuseState): string {
  if (a.reuse_until) return `〜${ymdSlash(fmtYmd(a.reuse_until) || a.reuse_until)}`;
  if (st === "requested") return "許諾依頼中";
  if (st === "none") return "許諾未取得";
  return "期限の記録なし";
}

export function AssetsTab(props: GfTabProps) {
  const { nav, nowIso, data } = props;
  const filter = nav.state.asset;
  const [assets, setAssets] = useState<Loadable<GfAsset[]>>({ status: "loading" });
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    setAssets((prev) => (prev.status === "ok" ? prev : { status: "loading" }));
    fetchGfAssets(filter)
      .then((d) => live && setAssets({ status: "ok", data: d }))
      .catch((e: unknown) => live && setAssets({ status: "error", message: errMsg(e) }));
    return () => {
      live = false;
    };
  }, [filter, reload, data.board]);

  return (
    <>
      <div className="gf-page-head">
        <h1 className="gf-h1">2次利用素材庫</h1>
        <div className="gf-chips gf-chips-sm">
          {FILTERS.map((f) => (
            <GfChip key={f.value} dark active={filter === f.value} onClick={() => nav.set({ asset: f.value })}>
              {f.label}
            </GfChip>
          ))}
        </div>
      </div>

      <DetectedPosts {...props} />

      {assets.status === "error" && <GfErrorBanner message={`素材を取得できませんでした（${assets.message}）`} onRetry={() => setReload((r) => r + 1)} />}
      {assets.status === "loading" && (
        <div className="gf-asset-grid">
          {[0, 1, 2, 3].map((i) => (
            <GfSkeleton key={i} height={240} />
          ))}
        </div>
      )}
      {assets.status === "ok" &&
        (assets.data.length === 0 ? (
          <GfEmpty>該当する素材はありません</GfEmpty>
        ) : (
          <div className="gf-asset-grid">
            {assets.data.map((a) => {
              const st = reuseState(a, nowIso);
              const uses = reuseScopeLabels(a.reuse_scope);
              return (
                <a
                  className="gf-asset"
                  key={a.id}
                  href={a.post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={[a.campaign_name, a.product_name].filter(Boolean).join(" / ")}
                >
                  <div className={`gf-asset-thumb${a.thumbnail_url ? "" : " gf-placeholder"}`} style={{ aspectRatio: ratioOf(a) }}>
                    {a.thumbnail_url && <img src={a.thumbnail_url} alt="" loading="lazy" referrerPolicy="no-referrer" />}
                    <span className="gf-asset-kind">{kindLabel(a)}</span>
                    <span className="gf-asset-status">
                      <GfTag tone={STATE_TONE[st]}>{REUSE_STATE_LABEL[st]}</GfTag>
                    </span>
                  </div>
                  <div className="gf-asset-body">
                    <span className="gf-name-strong">{a.influencer_name}</span>
                    <div className="gf-use-chips">
                      {uses.length === 0 ? <span className="gf-faint gf-small">用途未設定</span> : uses.map((u) => (
                        <span className="gf-use-chip" key={u}>
                          {u}
                        </span>
                      ))}
                    </div>
                    <span className="gf-note">{termText(a, st)}</span>
                  </div>
                </a>
              );
            })}
          </div>
        ))}
    </>
  );
}
